import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToastService } from './toast';

/**
 * Downloads the partner welcome / subscription letter PDF (GET /api/HospicashLetter/Download) through
 * HttpClient so the auth interceptor attaches the JWT, then hands the blob to the browser. SSR-safe;
 * surfaces the backend's message (e.g. "for partner members only", "not issued") on failure.
 */
@Injectable({ providedIn: 'root' })
export class HospicashLetterService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private toast = inject(ToastService);
  private readonly api = environment.apiUrl;

  async download(memberId: number, ref?: string | number | null): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const blob = await firstValueFrom(
        this.http.get(`${this.api}/HospicashLetter/Download`, {
          params: { memberId },
          responseType: 'blob',
        }),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WelcomeLetter-${(ref ?? memberId).toString().replace(/[^\w-]/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err: any) {
      this.toast.error((await this.readBlobError(err)) || 'Could not generate the welcome letter.');
    }
  }

  /** The error envelope arrives as a Blob (responseType:'blob') — read + parse it for `.message`. */
  private async readBlobError(err: any): Promise<string> {
    try {
      const body = err?.error;
      if (body instanceof Blob) {
        const text = await body.text();
        try {
          return JSON.parse(text)?.message ?? text;
        } catch {
          return text;
        }
      }
      return body?.message ?? err?.message ?? '';
    } catch {
      return '';
    }
  }
}
